import {
    Alert,
    Autocomplete,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
} from "@mui/material";
import React, { useState } from "react";
import { PRIMARY_COLOR, primaryButtonSx } from "./RelationPanel";
import { CELLPHONE_LENGTH, formatCellphone, getSedeLabel, isCellphoneInput, isCompleteCellphone } from "./commerceRelations";

const EMPTY_SEDE = { name: "", address: "", city: "", department: "", cellphone: "" };
const EMPTY_ASESOR = {
    name: "",
    lastName: "",
    documentNumber: "",
    email: "",
    isActive: true,
    storeCommerceUuid: "",
    commerceCellphoneUuid: "",
};
const SELECT_MENU_PROPS = { PaperProps: { style: { maxHeight: 300 } } };
const CELLPHONE_INPUT_PROPS = { maxLength: CELLPHONE_LENGTH, inputMode: "numeric" };
const EDIT_SUBMIT_LABEL = "Guardar cambios";

const pickFields = (source, emptyForm) =>
    Object.keys(emptyForm).reduce(
        (form, field) => ({ ...form, [field]: source?.[field] ?? emptyForm[field] }),
        {}
    );

const useDialogForm = (initialForm, onSubmit, onClose) => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const setField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: undefined }));
    };

    const close = () => {
        if (!isSaving) {
            onClose();
        }
    };

    const submit = (validate) => {
        const validationErrors = validate(form);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setIsSaving(true);
        setSubmitError("");
        onSubmit(form)
            .then(() => {
                setIsSaving(false);
                onClose();
            })
            .catch((error) => {
                setIsSaving(false);
                setSubmitError(error.message);
            });
    };

    return { form, errors, submitError, isSaving, setField, close, submit };
};

function FormDialog({ open, title, description, submitLabel, isSaving, submitError, onClose, onSubmit, children }) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="relation-form-title">
            <DialogTitle id="relation-form-title">{title}</DialogTitle>
            <DialogContent dividers>
                {description && <DialogContentText mb={2}>{description}</DialogContentText>}
                {submitError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {submitError}
                    </Alert>
                )}
                <Grid container spacing={2}>
                    {children}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving} sx={{ color: PRIMARY_COLOR, textTransform: "none" }}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={primaryButtonSx}
                >
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const validateSede = (sede) => {
    const errors = {};
    if (!sede.address.trim()) {
        errors.address = "La dirección es obligatoria";
    }
    if (sede.cellphone && !isCompleteCellphone(sede.cellphone)) {
        errors.cellphone = `Debe tener ${CELLPHONE_LENGTH} dígitos`;
    }
    return errors;
};

export function SedeFormDialog({ open, onClose, onSubmit, cities, departaments, sede }) {
    const isEdit = Boolean(sede);
    const { form, errors, submitError, isSaving, setField, close, submit } = useDialogForm(
        pickFields(sede, EMPTY_SEDE),
        onSubmit,
        onClose
    );

    return (
        <FormDialog
            open={open}
            title={isEdit ? "Editar sede" : "Agregar sede"}
            description="Solo la dirección es obligatoria. El nombre ayuda a identificarla rápido."
            submitLabel={isEdit ? EDIT_SUBMIT_LABEL : "Guardar sede"}
            isSaving={isSaving}
            submitError={submitError}
            onClose={close}
            onSubmit={() => submit(validateSede)}
        >
            <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Nombre de la sede" placeholder="Ej: Sede Centro" value={form.name} onChange={(e) => setField("name", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    required
                    label="Dirección"
                    value={form.address}
                    onChange={(e) => setField("address", e.target.value)}
                    error={Boolean(errors.address)}
                    helperText={errors.address}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                    <InputLabel id="sede-department-label">Departamento</InputLabel>
                    <Select
                        labelId="sede-department-label"
                        label="Departamento"
                        value={form.department}
                        onChange={(e) => setField("department", e.target.value)}
                        MenuProps={SELECT_MENU_PROPS}
                    >
                        {departaments.map((department) => (
                            <MenuItem key={department.uuid} value={department.name}>
                                {department.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    options={cities}
                    value={form.city || null}
                    onChange={(event, city) => setField("city", city || "")}
                    renderInput={(params) => <TextField {...params} label="Ciudad" />}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label="Celular de la sede"
                    value={form.cellphone}
                    onChange={(e) => isCellphoneInput(e.target.value) && setField("cellphone", e.target.value)}
                    inputProps={CELLPHONE_INPUT_PROPS}
                    error={Boolean(errors.cellphone)}
                    helperText={errors.cellphone}
                />
            </Grid>
        </FormDialog>
    );
}

export function CellphoneFormDialog({ open, onClose, onSubmit, existingNumbers, cellphone }) {
    const isEdit = Boolean(cellphone);
    const { form, errors, submitError, isSaving, setField, close, submit } = useDialogForm(
        { number: cellphone?.number || "" },
        onSubmit,
        onClose
    );

    const validateCellphone = ({ number }) => {
        if (!isCompleteCellphone(number)) {
            return { number: `Ingresa los ${CELLPHONE_LENGTH} dígitos del celular` };
        }
        if (existingNumbers.includes(number)) {
            return { number: `El ${formatCellphone(number)} ya está registrado en este comercio` };
        }
        return {};
    };

    return (
        <FormDialog
            open={open}
            title={isEdit ? "Editar celular" : "Agregar celular"}
            description={
                isEdit
                    ? "Los asesores que usan este celular quedarán con el número nuevo."
                    : "El número se guarda de inmediato y queda disponible para asignarlo a un asesor."
            }
            submitLabel={isEdit ? EDIT_SUBMIT_LABEL : "Guardar celular"}
            isSaving={isSaving}
            submitError={submitError}
            onClose={close}
            onSubmit={() => submit(validateCellphone)}
        >
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    autoFocus
                    required
                    label="Número de celular"
                    placeholder="3001234567"
                    value={form.number}
                    onChange={(e) => isCellphoneInput(e.target.value) && setField("number", e.target.value)}
                    inputProps={CELLPHONE_INPUT_PROPS}
                    error={Boolean(errors.number)}
                    helperText={errors.number || `${form.number.length}/${CELLPHONE_LENGTH} dígitos`}
                />
            </Grid>
        </FormDialog>
    );
}

const validateAsesor = (asesor) => {
    const errors = {};
    if (!asesor.name.trim()) {
        errors.name = "El nombre es obligatorio";
    }
    if (!asesor.storeCommerceUuid) {
        errors.storeCommerceUuid = "Selecciona la sede del asesor";
    }
    if (!asesor.commerceCellphoneUuid) {
        errors.commerceCellphoneUuid = "Selecciona el celular del asesor";
    }
    return errors;
};

export function AsesorFormDialog({ open, onClose, onSubmit, sedes, cellphones, asesor }) {
    const isEdit = Boolean(asesor);
    const { form, errors, submitError, isSaving, setField, close, submit } = useDialogForm(
        pickFields(asesor, EMPTY_ASESOR),
        onSubmit,
        onClose
    );

    return (
        <FormDialog
            open={open}
            title={isEdit ? "Editar asesor" : "Agregar asesor"}
            description={
                isEdit
                    ? "Un asesor inactivo deja de atender desde su celular."
                    : "El asesor queda activo y vinculado a la sede y al celular que elijas."
            }
            submitLabel={isEdit ? EDIT_SUBMIT_LABEL : "Guardar asesor"}
            isSaving={isSaving}
            submitError={submitError}
            onClose={close}
            onSubmit={() => submit(validateAsesor)}
        >
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    required
                    label="Nombre"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Apellido" value={form.lastName} onChange={(e) => setField("lastName", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Número de documento" value={form.documentNumber} onChange={(e) => setField("documentNumber", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField fullWidth type="email" label="Email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={Boolean(errors.storeCommerceUuid)}>
                    <InputLabel id="asesor-sede-label">Sede</InputLabel>
                    <Select
                        labelId="asesor-sede-label"
                        label="Sede"
                        value={form.storeCommerceUuid}
                        onChange={(e) => setField("storeCommerceUuid", e.target.value)}
                        MenuProps={SELECT_MENU_PROPS}
                    >
                        {sedes.map((sede) => (
                            <MenuItem key={sede.uuid} value={sede.uuid}>
                                {getSedeLabel(sede) || sede.uuid}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.storeCommerceUuid && <FormHelperText>{errors.storeCommerceUuid}</FormHelperText>}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={Boolean(errors.commerceCellphoneUuid)}>
                    <InputLabel id="asesor-cellphone-label">Celular</InputLabel>
                    <Select
                        labelId="asesor-cellphone-label"
                        label="Celular"
                        value={form.commerceCellphoneUuid}
                        onChange={(e) => setField("commerceCellphoneUuid", e.target.value)}
                        MenuProps={SELECT_MENU_PROPS}
                    >
                        {cellphones.map((commerceCellphone) => (
                            <MenuItem key={commerceCellphone.uuid} value={commerceCellphone.uuid}>
                                {formatCellphone(commerceCellphone.number)}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.commerceCellphoneUuid && <FormHelperText>{errors.commerceCellphoneUuid}</FormHelperText>}
                </FormControl>
            </Grid>
            {isEdit && (
                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Switch checked={form.isActive} onChange={(e) => setField("isActive", e.target.checked)} />}
                        label={form.isActive ? "Activo" : "Inactivo"}
                    />
                </Grid>
            )}
        </FormDialog>
    );
}
